import { useEffect, useRef } from "react";

type UseFocusAwarePollingParams = {
    enabled: boolean;
    intervalMs: number;
    onPoll: () => void;
    /** Run once when polling starts (default: true). */
    pollImmediately?: boolean;
};

/**
 * Runs `onPoll` on an interval while `enabled`, only when the document tab is focused and visible.
 */
export function useFocusAwarePolling({
    enabled,
    intervalMs,
    onPoll,
    pollImmediately = true,
}: UseFocusAwarePollingParams) {
    const onPollRef = useRef(onPoll);
    onPollRef.current = onPoll;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const poll = () => onPollRef.current();

        if (pollImmediately) {
            poll();
        }

        const interval = setInterval(() => {
            if (document.hasFocus() && !document.hidden) {
                poll();
            }
        }, intervalMs);

        return () => clearInterval(interval);
    }, [enabled, intervalMs, pollImmediately]);
}
